import React, { useState } from 'react';
import { ScenarioType } from './ScenarioEditor';
import { GripVertical, Plus, Trash2, Edit2, BarChart2, ChevronUp, ChevronDown } from 'lucide-react';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ForecastMetrics, ScenarioData } from '../../interface/ReasearchData';
interface ScenarioDetailProps {
  type: ScenarioType;
  scenarioData: ScenarioData[];
  history: ScenarioData[];
  onUpdate: (metric: keyof ScenarioData, year: string, value: string, label: string, index: number) => void;
}
interface MetricRow {
  id: string;
  name: string;
  dataKey: keyof ScenarioData;
  editable: boolean;
}
export function ScenarioDetail({
  type,
  scenarioData,
  onUpdate,
  history
}: ScenarioDetailProps) {
  const years = history.map((item: ScenarioData) => ({
    year: item.fiscalYear,
    label: item.yearLabel
  }));

  const [metrics, setMetrics] = useState<ForecastMetrics[]>([{
    id: 'revenue',
    name: 'Revenue',
    editable: false
  }, {
    id: 'revenueGrowth',
    name: 'Revenue Growth',
    editable: true
  }, {
    id: 'eps',
    name: 'EPS',
    editable: false
  },
  // {
  //   id: 'epsGrowth',
  //   name: 'EPS Growth',
  //   editable: true
  // },
  
  // {
  //   id: 'costOfRevenue',
  //   name: 'Cost of Revenue',
  //   editable: false
  // },
  // {
  //   id: 'grossProfit',
  //   name: 'Gross Profit',
  //   editable: false
  // },
  {
    id: "netIncome",
    name: 'Net Income',
    editable: false
  },
  {
    id: 'netIncomeGrowth',
    name: 'Net Income Growth',
    editable: true
  },
  // {
  //   id: "netMargins",
  //   name: 'Net Income Margin',
  //   editable: false
  // },
  {
    id: 'peLow',
    name: 'P/E Low',
    editable: true
  },
  {
    id: 'peHigh',
    name: 'P/E High',
    editable: true
  },
  {
    id: 'sharesOutstanding',
    name: 'Shares Outstanding',
    editable: false
  },
  {
    id: 'sharePriceLow',
    name: 'Share Price Low',
    editable: false
  },
  {
    id: 'sharePriceHigh',
    name: 'Share Price High',
    editable: false
  },
  

  ]);
  // State for showing add metric dialog
  const [showAddMetric, setShowAddMetric] = useState(false);
  const [newMetricName, setNewMetricName] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const getScenarioColor = () => {
    switch (type) {
      case 'bull':
        return 'green';
      case 'base':
        return 'blue';
      case 'bear':
        return 'red';
      default:
        return 'gray';
    }
  };
  // Explicitly define background colors for different scenarios to avoid dynamic class issues
  const getHighlightBgClass = () => {
    switch (type) {
      case 'bull':
        return 'bg-green-900/20';
      case 'base':
        return 'bg-blue-900/20';
      case 'bear':
        return 'bg-red-900/20';
      default:
        return 'bg-theme-primary/20';
    }
  };
  const getPriceTextClass = () => {
    switch (type) {
      case 'bull':
        return 'text-green-500';
      case 'base':
        return 'text-blue-500';
      case 'bear':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };
  const handleInputChange = (metric: keyof ScenarioData, year: string, value: string, label: string, index: number) => {
    // console.log("Input changed:", { metric, year, value, label });
    onUpdate(metric, year, value, label, index);
  };
  // const handleDragEnd = (result: any) => {
  //   if (!result.destination) return;
  //   const items = Array.from(metrics);
  //   const [reorderedItem] = items.splice(result.source.index, 1);
  //   items.splice(result.destination.index, 0, reorderedItem);
  //   setMetrics(items);
  // };
  const addCustomMetric = () => {
    if (!newMetricName.trim()) return;
    const id = `custom_${Date.now()}`;
    const newMetric: MetricRow = {
      id,
      name: newMetricName.toUpperCase(),
      dataKey: id as keyof ScenarioData,
      editable: true
    };
    // Initialize data for the new metric
    const metricData: Record<string, string> = {};
    // years.forEach(year => {
    //   metricData[year] = '0';
    // });
    // onUpdate({
    //   [id]: metricData
    // } as unknown as Partial<ScenarioData>);
    setMetrics([...metrics, newMetric]);
    setNewMetricName('');
    setShowAddMetric(false);
  };
  // const removeMetric = (index: number) => {
  //   const newMetrics = [...metrics];
  //   newMetrics.splice(index, 1);
  //   setMetrics(newMetrics);
  // };
  const getEditable = (metricId: string) => {
    const editable = metrics.find(m => m.id === metricId)?.editable;
    return editable;
  };
  const getCellStyle = (metricId: string, value: string) => {
    if (value === '0' || value === '') return '';
    if (metricId === 'revGrowth' || metricId === 'netIncGrowth') {
      const numValue = parseFloat(value.replace('%', ''));
      if (numValue > 0) return 'text-green-500';
      if (numValue < 0) return 'text-red-500';
    }
    return '';
  };
  const isHighlightedYear = (year: string) => {
    return year === '2026' || year === '2027' || year === '2028';
  };
  const highlightBgClass = getHighlightBgClass();
  const priceTextClass = getPriceTextClass();
  return <div className="bg-theme-secondary rounded-lg shadow-lg overflow-hidden mb-4">
    <div className="p-2.5 flex justify-between items-center border-b border-theme-tertiary cursor-pointer hover:bg-theme-tertiary/70 transition-colors" onClick={() => setIsCollapsed(!isCollapsed)}>
      <div className="flex items-center">
        <BarChart2 size={14} className={`text-${getScenarioColor()}-400 mr-1.5`} />
        <h2 className="text-sm font-semibold text-theme-primary">
          {type.charAt(0).toUpperCase() + type.slice(1)} Case Forecast
        </h2>
      </div>
      {/* <div className="flex items-center space-x-2">
        <button onClick={e => {
          e.stopPropagation();
          setShowAddMetric(!showAddMetric);
        }} className="p-1.5 bg-theme-tertiary hover:bg-theme-tertiary/70 text-theme-primary rounded-full transition-colors" title="Add metric">
          <Plus size={14} />
        </button>
        <div className="text-theme-muted hover:text-theme-primary transition-colors">
          {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </div>
      </div> */}
    </div>
    {showAddMetric && !isCollapsed && <div className="p-3 bg-theme-tertiary/50 border-b border-theme-tertiary flex items-center gap-2">
      <input type="text" value={newMetricName} onChange={e => setNewMetricName(e.target.value)} placeholder="Enter metric name" className="flex-1 bg-theme-secondary border border-theme-tertiary rounded px-2 py-1 text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <button onClick={addCustomMetric} className="bg-blue-600 hover:bg-blue-700 text-theme-primary px-2 py-1 rounded text-xs transition-colors">
        Add Metric
      </button>
      <button onClick={() => setShowAddMetric(false)} className="bg-theme-tertiary hover:bg-theme-tertiary/70 text-theme-secondary hover:text-theme-primary px-2 py-1 rounded text-xs transition-colors border border-theme-tertiary">
        Cancel
      </button>
    </div>}
    {!isCollapsed && <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-theme-secondary border-b border-theme-tertiary">
            <th className="p-2 font-medium text-xs text-theme-muted sticky left-0 bg-theme-secondary z-10">
              Metric
            </th>
            {years.map((year: any, index: number) => <th key={`${year.year}-${index}`} className={`p-2 font-medium text-center text-xs text-theme-muted ${isHighlightedYear(year) ? highlightBgClass : ''}`}>
              {year.label}
            </th>)}
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric: ForecastMetrics, index: number) => {
            return <tr key={`${metric.id}-${index}`} className={`border-b border-theme-tertiary ${hoveredRow === metric.id ? 'bg-theme-tertiary/50' : ''}`} onMouseEnter={() => setHoveredRow(metric.id)} onMouseLeave={() => setHoveredRow(null)}>
              <td className={`p-2 text-xs text-theme-primary font-medium sticky left-0 z-10 bg-theme-secondary flex items-center gap-2 ${isHighlightedYear(metric.id) ? highlightBgClass : ''}`}>
                {metric.name}
              </td>
              {years.map((year: any, index: number) => {
                let value:any = 0; let formattedValue = '';
                // if (year.label === 'TTM' || year.year < new Date().getFullYear()) {
                //   let data: any = history?.find((item) => item.yearLabel === year.label)?.[metric.id as keyof ScenarioData] || '';
                //   value = data?.value || '';
                //   formattedValue = data?.formattedValue || '';
                // } else {
                  let scenarioData_ = scenarioData?.find((item: any) => item.yearLabel === year.label)
                  let data: any = scenarioData_?.[metric.id as keyof ScenarioData] || '';
                  value = data.value || "";
                  formattedValue = data.formattedValue || '';
                // }
                return <td key={`${metric.id}-${year.year}-${index}`} className={`p-2 text-center text-xs ${isHighlightedYear(year) ? highlightBgClass : ''}`}>
                  {getEditable(metric.id) && scenarioData_?.isEditableYear ? <input type="text" placeholder='Enter Value' value={value != ""? value : ''} onChange={e => handleInputChange(metric.id as keyof ScenarioData, year.year, e.target.value, year.label, index)} className="w-full input-theme rounded px-2 py-1 text-center text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" /> : <div className={`
                    w-full bg-theme-tertiary/50 rounded px-2 py-1 text-center text-xs
                    ${metric.id.includes('Price') ? priceTextClass + ' font-medium' : getCellStyle(metric.id, value)}
                  `}>
                    {formattedValue || '-'}
                  </div>}
                </td>;
              })}
            </tr>;
          })}
        </tbody>
        {/* <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="metrics">
            {(provided: any) => <tbody {...provided.droppableProps} ref={provided.innerRef}>
              {metrics.map((metric, index) => <Draggable key={metric.id} draggableId={metric.id} index={index}>
                {(provided: any) => <tr ref={provided.innerRef} {...provided.draggableProps} className="border-b border-theme-tertiary hover:bg-theme-tertiary/70 transition-colors" onMouseEnter={() => setHoveredRow(metric.id)} onMouseLeave={() => setHoveredRow(null)}>
                  <td className="p-2 text-xs text-theme-primary font-medium sticky left-0 z-10 bg-theme-secondary flex items-center gap-2" {...provided.dragHandleProps}>
                    <GripVertical size={14} className="text-theme-muted cursor-grab" />
                    {metric.name}
                  </td>
                  {years.map((year: any) => {
                    const value = data[metric.id][year?.year] || 0;
                    const cellStyle = getCellStyle(metric.id, value);
                    return <td key={year.year} className={`p-2 text-center text-xs ${isHighlightedYear(year) ? highlightBgClass : ''}`}>
                      {metric.editable ? <input type="text" value={value} onChange={e => handleInputChange(metric.id, year, e.target.value)} className="w-full bg-theme-tertiary border border-theme-tertiary rounded px-2 py-1 text-theme-primary text-center text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" /> : <div className={`
                                        w-full bg-theme-tertiary/50 rounded px-2 py-1 text-center text-xs
                                        ${metric.id.includes('Price') ? priceTextClass + ' font-medium' : `${cellStyle || 'text-theme-secondary'}`}
                                      `}>
                        {metric.id.includes('Price') ? '$' : ''}
                        {formatValue(metric.id, value)}
                      </div>}
                      {hoveredRow === metric.id && !metric.editable && <button className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-theme-tertiary hover:bg-theme-tertiary/70 p-0.5 rounded opacity-50 hover:opacity-100" title="View chart">
                        <BarChart2 size={10} className="text-theme-muted" />
                      </button>}
                    </td>;
                  })}
                  <td className="p-2">
                    {!['revenue', 'netIncome', 'eps', 'sharePriceLow', 'sharePriceHigh'].includes(metric.id) && <button onClick={e => {
                      e.stopPropagation();
                      removeMetric(index);
                    }} className="p-1 text-theme-muted hover:text-red-500 rounded-full hover:bg-theme-tertiary transition-colors" title="Remove metric">
                      <Trash2 size={14} />
                    </button>}
                  </td>
                </tr>}
              </Draggable>)}
              {provided.placeholder}
            </tbody>}
          </Droppable>
        </DragDropContext> */}
      </table>
    </div>}
    <div className="p-3 text-xs text-theme-muted border-t border-theme-tertiary">
      <p>
        Editable fields have input boxes. Read-only fields are calculated
        automatically.
      </p>
    </div>
  </div>;
}
// function formatValue(metricId: string, value: string): string {
//   if (!value || value === '0') return '0';
//   if (metricId === 'revenue' || metricId === 'netIncome') {
//     const num = parseFloat(value);
//     if (isNaN(num)) return value;
//     if (num >= 1e9) {
//       return (num / 1e9).toFixed(2) + 'B';
//     } else if (num >= 1e6) {
//       return (num / 1e6).toFixed(2) + 'M';
//     }
//   }
//   if (metricId.includes('Growth') || metricId.includes('Margins')) {
//     if (!value.includes('%')) {
//       return value + '%';
//     }
//   }
//   return value;
// }